/**
 * SourceApplicationModal Component
 * Modal for setting source generator application details (exposure level, dates, etc.)
 *
 * @version 2025.1.0
 */

import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  SourceApplicationData,
  AvailableSource,
  SourceWithApplication,
  SourceExposureLevel,
  SourceExposureLevelLabels,
  SourceExposureLevelColors,
  isSourceWithApplication,
  RiskTypeLabels,
} from '../../types/sourceApplication';

// ============================================================================
// TYPES
// ============================================================================

export interface SourceApplicationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when form is submitted */
  onConfirm: (data: SourceApplicationData) => Promise<void>;
  /** The source generator being applied/edited */
  source: AvailableSource | SourceWithApplication | null;
  /** Mode: 'apply' for new application, 'edit' for existing */
  mode: 'apply' | 'edit';
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
}

interface FormValues {
  exposureLevel: SourceExposureLevel;
  startDate: Date | null;
  endDate: Date | null;
  observations: string;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

/**
 * "Não pode ser no futuro", conferido **na hora de validar** e não na carga do módulo.
 *
 * `.max(new Date(), ...)` parecia a mesma coisa e não era: o esquema é montado uma vez, quando o
 * módulo carrega, então aquele `new Date()` virava um teto congelado no instante da carga da
 * página. O valor padrão do campo é o instante em que a caixa abre — sempre depois. Bastavam
 * segundos de aba aberta para o campo reprovar o próprio valor padrão.
 */
const NAO_E_FUTURO = 'nao-e-futuro';
const aindaNaoAconteceu = (valor: Date | null | undefined): boolean =>
  !valor || valor.getTime() <= Date.now();

const validationSchema: yup.ObjectSchema<FormValues> = yup.object({
  exposureLevel: yup
    .mixed<SourceExposureLevel>()
    .oneOf(Object.values(SourceExposureLevel), 'Nível de exposição inválido')
    .required('Nível de exposição é obrigatório')
    .defined(),
  startDate: yup
    .date()
    .nullable()
    .defined()
    .test(NAO_E_FUTURO, 'Data de inicio nao pode ser no futuro', aindaNaoAconteceu),
  endDate: yup
    .date()
    .nullable()
    .defined()
    .when('startDate', {
      is: (val: Date | null) => val !== null,
      then: (schema) =>
        schema.min(
          yup.ref('startDate'),
          'Data de fim deve ser posterior a data de inicio'
        ),
    }),
  observations: yup.string().defined().max(2000, 'Observacoes nao podem exceder 2000 caracteres'),
});

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface ExposureLevelChipProps {
  level: SourceExposureLevel;
  selected?: boolean;
}

const ExposureLevelChip: React.FC<ExposureLevelChipProps> = ({ level, selected }) => (
  <Chip
    label={SourceExposureLevelLabels[level]}
    color={SourceExposureLevelColors[level]}
    variant={selected ? 'filled' : 'outlined'}
    size="small"
    sx={{ minWidth: 80 }}
  />
);

// ============================================================================
// COMPONENT
// ============================================================================

export const SourceApplicationModal: React.FC<SourceApplicationModalProps> = ({
  open,
  onClose,
  onConfirm,
  source,
  mode,
  loading = false,
  error = null,
}) => {
  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      exposureLevel: SourceExposureLevel.MEDIUM,
      startDate: null,
      endDate: null,
      observations: '',
    },
  });

  // Reset form when modal opens/closes or source changes
  useEffect(() => {
    if (open && source) {
      if (isSourceWithApplication(source)) {
        // Editing existing application
        reset({
          exposureLevel: source.exposureLevel,
          startDate: source.startDate ? new Date(source.startDate) : null,
          endDate: source.endDate ? new Date(source.endDate) : null,
          observations: source.observations ?? '',
        });
      } else {
        // New application
        reset({
          exposureLevel: SourceExposureLevel.MEDIUM,
          startDate: new Date(), // Default to today
          endDate: null,
          observations: '',
        });
      }
    }
  }, [open, source, reset]);

  // Submit handler
  const onSubmit = async (data: FormValues) => {
    const applicationData: SourceApplicationData = {
      exposureLevel: data.exposureLevel,
      startDate: data.startDate?.toISOString().split('T')[0] ?? null,
      endDate: data.endDate?.toISOString().split('T')[0] ?? null,
      observations: data.observations ?? null,
    };

    await onConfirm(applicationData);
  };

  // Get source name for display
  const sourceName = source
    ? isSourceWithApplication(source)
      ? source.sourceName
      : source.name
    : 'Fonte Geradora';

  const riskType = source?.riskType ?? '';
  const riskTypeLabel = riskType && RiskTypeLabels[riskType] ? RiskTypeLabels[riskType] : riskType;

  return (
    <Dialog
      open={open}
      onClose={loading || isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {mode === 'apply' ? 'Aplicar Fonte Geradora' : 'Editar Aplicacao'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sourceName}
          {riskTypeLabel && (
            <Chip
              label={riskTypeLabel}
              size="small"
              sx={{ ml: 1 }}
              variant="outlined"
            />
          )}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Stack spacing={3}>
              {error && (
                <Alert severity="error" onClose={() => {}}>
                  {error}
                </Alert>
              )}

              {/* Exposure Level */}
              <Controller
                name="exposureLevel"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.exposureLevel}>
                    <InputLabel>Nível de Exposição</InputLabel>
                    <Select
                      {...field}
                      label="Nível de Exposição"
                      disabled={loading || isSubmitting}
                      renderValue={(value) => (
                        <ExposureLevelChip level={value as SourceExposureLevel} selected />
                      )}
                    >
                      {Object.values(SourceExposureLevel).map((level) => (
                        <MenuItem key={level} value={level}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ExposureLevelChip level={level} />
                            <Typography variant="body2" color="text.secondary">
                              {getExposureLevelDescription(level)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.exposureLevel && (
                      <FormHelperText>{errors.exposureLevel.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Start Date */}
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="Data de inicio da exposicao"
                    disabled={loading || isSubmitting}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.startDate,
                        helperText: errors.startDate?.message || 'Quando o trabalhador comecou a ser exposto',
                      },
                    }}
                  />
                )}
              />

              {/* End Date */}
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="Data de fim da exposicao (opcional)"
                    disabled={loading || isSubmitting}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.endDate,
                        helperText:
                          errors.endDate?.message ||
                          'Deixe em branco se a exposicao ainda esta ativa',
                      },
                    }}
                  />
                )}
              />

              {/* Observations */}
              <Controller
                name="observations"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Observacoes"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.observations}
                    helperText={
                      errors.observations?.message ||
                      `${field.value?.length ?? 0}/2000 caracteres`
                    }
                    disabled={loading || isSubmitting}
                    placeholder="Detalhes sobre a exposicao, medidas de controle, EPIs utilizados..."
                  />
                )}
              />

              {/* Info box */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'warning.50',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'warning.200',
                }}
              >
                <Typography variant="body2" color="warning.800">
                  <strong>Importante:</strong> Niveis de exposicao ALTO e MUITO ALTO
                  serao destacados nos alertas de saude ocupacional.
                </Typography>
              </Box>
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading || isSubmitting}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || isSubmitting}
            startIcon={
              (loading || isSubmitting) && <CircularProgress size={16} />
            }
          >
            {mode === 'apply' ? 'Aplicar' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getExposureLevelDescription(level: SourceExposureLevel): string {
  switch (level) {
    case SourceExposureLevel.LOW:
      return 'Exposição ocasional ou em baixa intensidade';
    case SourceExposureLevel.MEDIUM:
      return 'Exposição regular dentro dos limites de tolerância';
    case SourceExposureLevel.HIGH:
      return 'Exposição frequente próximo aos limites de tolerância';
    case SourceExposureLevel.VERY_HIGH:
      return 'Exposição contínua acima dos limites de tolerância';
  }
}

export default SourceApplicationModal;
